import React, { useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import StudentList from "./components/StudentList";
import TeacherList from "./components/TeacherList";
import AttendancePage from "./pages/AttendancePage";
import FeesPage from "./pages/FeesPage";
import SalaryPage from "./pages/SalaryPage";
import HomePage from "./pages/HomePage";
import ReportsPage from "./pages/ReportsPage";
import InquiriesPage from "./pages/InquiriesPage";
import GalleryAdminPage from "./pages/GalleryAdminPage";
import TestMarksheetPage from "./pages/TestMarksheetPage";
import BirthdayWishesPage from "./pages/BirthdayWishesPage";
import LoginPage from "./pages/LoginPage";
import PublicSite from "./pages/PublicSite";
import {
  deleteStudent,
  deleteTeacher,
  getAttendance,
  getFestivals,
  getFees,
  getSalaries,
  getStudents,
  getTeachers
} from "./services/api";
import GlobalProfileModal from "./components/GlobalProfileModal";
import FestivalModal from "./components/FestivalModal";
import { showAppToast, confirmAppAction } from "./utils/feedback";
import { getFeeCoverageForMonth } from "./utils/feeCoverage";
import { formatDate } from "./utils/helpers";
import "./App.css";

const STORAGE_KEY = "bright-line-admin-session";
const THEME_KEY = "bright-line-theme";
const NAV_COLOR_KEY = "bright-line-nav-color";
const getNotificationReadKey = (admin) =>
  `bright-line-read-notifications-${admin?.username || admin?.name || "guest"}`;
const UPCOMING_HOLIDAY = {
  name: "Buddha Purnima",
  date: "2026-05-01"
};
const formatToday = () =>
  `${new Date().toLocaleDateString("en-IN", { weekday: "short" })}, ${formatDate(new Date().toISOString().split("T")[0])}`;
const getTodayDate = () => new Date().toISOString().split("T")[0];
const getCurrentMonth = () => getTodayDate().slice(0, 7);
const getIstTime = () =>
  new Date().toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
const formatHolidayDate = (value) => formatDate(value);

const PUBLIC_ROUTE_MAP = {
  about: "/about-us",
  faculty: "/faculty",
  gallery: "/gallery",
  contact: "/contact-us",
  login: "/login"
};

const ADMIN_ROUTE_MAP = {
  home: "/app/home",
  students: "/app/students",
  teachers: "/app/teachers",
  attendance: "/app/attendance",
  inquiries: "/app/inquiries",
  financeFees: "/app/finance/fees",
  financePay: "/app/finance/pay",
  galleryAdmin: "/app/dashboard/gallery",
  testMarksheet: "/app/dashboard/test-marksheet",
  birthdayWishes: "/app/dashboard/birthday-wishes",
  reports: "/app/reports",
};

const PUBLIC_TITLE_MAP = {
  about: "BLC | About Us",
  faculty: "BLC | Faculty",
  gallery: "BLC | Gallery",
  contact: "BLC | Contact Us",
  login: "BLS | Login"
};

const ADMIN_TITLE_MAP = {
  home: "BLC | Dashboard",
  students: "BLC | Students",
  teachers: "BLC | Teachers",
  attendance: "BLC | Attendance",
  inquiries: "BLC | Inquiries",
  financeFees: "BLC | Student Fees",
  financePay: "BLC | Teachers Pay",
  galleryAdmin: "BLC | Gallery Management",
  testMarksheet: "BLC | Test / Marksheet",
  birthdayWishes: "BLC | Birthday Wishes",
  reports: "BLC | Reports"
};

const getInitialPublicPage = (pathname) => {
  switch (pathname) {
    case "/":
    case "/about-us":
      return "about";
    case "/faculty":
      return "faculty";
    case "/gallery":
      return "gallery";
    case "/contact-us":
      return "contact";
    case "/login":
      return "login";
    default:
      return "home";
  }
};

const getInitialAdminTab = (pathname) => {
  switch (pathname) {
    case "/app/students":
      return "students";
    case "/app/teachers":
      return "teachers";
    case "/app/attendance":
      return "attendance";
    case "/app/inquiries":
      return "inquiries";
    case "/app/finance/fees":
      return "financeFees";
    case "/app/finance/pay":
      return "financePay";
    case "/app/dashboard/gallery":
      return "galleryAdmin";
    case "/app/dashboard/test-marksheet":
      return "testMarksheet";
    case "/app/dashboard/birthday-wishes":
      return "birthdayWishes";
    case "/app/reports":
      return "reports";
    case "/app/home":
    default:
      return "home";
  }
};

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="app-shell__icon-svg"
    >
      <path d="M15 18H5.5a1.5 1.5 0 0 1-1.2-2.4l1.1-1.4a4 4 0 0 0 .9-2.5V10a5.7 5.7 0 0 1 11.4 0v1.7a4 4 0 0 0 .9 2.5l1.1 1.4a1.5 1.5 0 0 1-1.2 2.4H15" />
      <path d="M10 18a2 2 0 0 0 4 0" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="app-shell__search-icon"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState(() => getInitialAdminTab(window.location.pathname));
  const [publicPage, setPublicPage] = useState(() => getInitialPublicPage(window.location.pathname));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(window.innerWidth <= 1080);
  const [adminSession, setAdminSession] = useState(null);
  const [themeMode, setThemeMode] = useState("light");
  const [navColor, setNavColor] = useState("blue");
  const [moduleLoading, setModuleLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSearchItem, setSelectedSearchItem] = useState(null);
  const [globalProfileItem, setGlobalProfileItem] = useState(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readNotificationIds, setReadNotificationIds] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [showFestivalModal, setShowFestivalModal] = useState(false);
  const [currentIstTime, setCurrentIstTime] = useState(getIstTime());
  const [toastItems, setToastItems] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 1080;
      setIsMobileNav(nextIsMobile);
      setSidebarCollapsed(nextIsMobile);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storedSession = localStorage.getItem(STORAGE_KEY);
    const storedTheme = localStorage.getItem(THEME_KEY);
    const storedNavColor = localStorage.getItem(NAV_COLOR_KEY);

    if (storedSession) {
      try {
        setAdminSession(JSON.parse(storedSession));
      } catch (err) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    if (storedTheme) {
      setThemeMode(storedTheme);
    }

    if (storedNavColor) {
      setNavColor(storedNavColor);
    }
  }, []);

  useEffect(() => {
    if (!adminSession) {
      setReadNotificationIds([]);
      return;
    }

    const storageKey = getNotificationReadKey(adminSession);

    try {
      const storedReadIds = JSON.parse(localStorage.getItem(storageKey) || "[]");
      setReadNotificationIds(Array.isArray(storedReadIds) ? storedReadIds : []);
    } catch (error) {
      setReadNotificationIds([]);
    }
  }, [adminSession]);

  useEffect(() => {
    if (!adminSession) {
      return;
    }

    localStorage.setItem(getNotificationReadKey(adminSession), JSON.stringify(readNotificationIds));
  }, [adminSession, readNotificationIds]);

  useEffect(() => {
    const handlePopState = () => {
      const { pathname } = window.location;

      if (adminSession) {
        setActiveTab(getInitialAdminTab(pathname));
      } else {
        setPublicPage(getInitialPublicPage(pathname));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [adminSession]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem(NAV_COLOR_KEY, navColor);
  }, [navColor]);

  useEffect(() => {
    if (adminSession?.role !== "admin" && activeTab === "financePay") {
      setActiveTab("financeFees");
    }
  }, [adminSession, activeTab]);

  useEffect(() => {
    if (adminSession) {
      const safeActiveTab =
        adminSession?.role !== "admin" && activeTab === "financePay" ? "financeFees" : activeTab;
      const nextPath = ADMIN_ROUTE_MAP[safeActiveTab] || ADMIN_ROUTE_MAP.home;
      const nextTitle = ADMIN_TITLE_MAP[safeActiveTab] || ADMIN_TITLE_MAP.home;

      if (window.location.pathname !== nextPath) {
        window.history.replaceState({}, "", nextPath);
      }

      document.title = nextTitle;
      return;
    }

    const nextPath = PUBLIC_ROUTE_MAP[publicPage] || PUBLIC_ROUTE_MAP.about;
    const nextTitle = PUBLIC_TITLE_MAP[publicPage] || PUBLIC_TITLE_MAP.about;

    if (window.location.pathname !== nextPath) {
      window.history.replaceState({}, "", nextPath);
    }

    document.title = nextTitle;
  }, [adminSession, activeTab, publicPage]);

  useEffect(() => {
    if (!adminSession) {
      setNotifications([]);
      setReadNotificationIds([]);
      return;
    }

    const loadNotifications = async () => {
      try {
        const today = getTodayDate();
        const month = getCurrentMonth();
        const [studentsRes, attendanceRes, feesRes, salariesRes] = await Promise.all([
          getStudents("all", { fields: "lookup" }),
          getAttendance(today, "all"),
          getFees({}),
          adminSession.role === "admin" ? getSalaries({}) : Promise.resolve({ data: [] })
        ]);

        const studentMap = new Map(studentsRes.data.map((student) => [String(student._id), student]));
        const feesByStudentId = feesRes.data.reduce((map, record) => {
          const studentId = String(record.student_id);
          const records = map.get(studentId) || [];
          records.push(record);
          map.set(studentId, records);
          return map;
        }, new Map());
        const absentNotifications = attendanceRes.data
          .filter((record) => record.attendance_status === "absent")
          .map((record) => ({
            id: `absent-${record._id}`,
            type: "absent",
            title: `${record.student_name} is absent`,
            detail: `Class ${record.class} | ${formatDate(record.date)}`
          }));

        const feeNotifications = studentsRes.data.flatMap((student) => {
          const studentFeeRecords = feesByStudentId.get(String(student._id)) || [];
          const feeCoverage = getFeeCoverageForMonth(studentFeeRecords, month);
          const monthRecord = feeCoverage.currentRecord;

          if (!feeCoverage.isPaid) {
            return [{
              id: `fee-${student._id}`,
              type: "fees",
              title: `Fees pending for ${student.name}`,
              detail: monthRecord
                ? `${monthRecord.billingCycle || "monthly"} fee from ${formatDate(monthRecord.dueDate)} is ${monthRecord.status}`
                : feeCoverage.helper
            }];
          }

          return [];
        });

        const salaryNotifications = adminSession.role === "admin"
          ? salariesRes.data.reduce((acc, record) => {
              return acc;
            }, [])
          : [];

        const teacherIdsWithCurrentMonthPaid = new Set(
          salariesRes.data
            .filter((record) => String(record.payPeriod || "").slice(0, 7) === month && record.status === "paid")
            .map((record) => String(record.teacher_id))
        );
        const teacherIdsWithCurrentMonthAny = new Set(
          salariesRes.data
            .filter((record) => String(record.payPeriod || "").slice(0, 7) === month)
            .map((record) => String(record.teacher_id))
        );
        const teacherSalaryNotifications =
          adminSession.role === "admin"
            ? (await getTeachers("all", { fields: "lookup" })).data.flatMap((teacher) => {
                const teacherId = String(teacher._id);
                if (teacherIdsWithCurrentMonthPaid.has(teacherId)) {
                  return [];
                }

                const currentMonthRecord = salariesRes.data.find(
                  (record) =>
                    String(record.teacher_id) === teacherId &&
                    String(record.payPeriod || "").slice(0, 7) === month
                );

                return [{
                  id: `salary-${teacher._id}`,
                  type: "salary",
                  title: `Salary not paid for ${teacher.name}`,
                  detail: currentMonthRecord
                    ? `Current month status is ${currentMonthRecord.status}`
                    : `No salary record for ${month}`
                }];
              })
            : [];

        void studentMap;
        void salaryNotifications;
        void teacherIdsWithCurrentMonthAny;

        const nextNotifications = [...feeNotifications, ...absentNotifications, ...teacherSalaryNotifications];

        setNotifications(nextNotifications);
      } catch (err) {
        console.error("Failed to load notifications:", err);
        setNotifications([]);
      }
    };

    loadNotifications();
    const intervalId = window.setInterval(loadNotifications, 60000);
    return () => window.clearInterval(intervalId);
  }, [adminSession]);

  useEffect(() => {
    const loadFestivals = async () => {
      try {
        const res = await getFestivals(getTodayDate());
        setFestivals(res.data);
      } catch (err) {
        console.error("Failed to load festivals:", err);
        setFestivals([]);
      }
    };

    loadFestivals();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentIstTime(getIstTime());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleToast = (event) => {
      const toast = event.detail;
      if (!toast) {
        return;
      }

      setToastItems((current) => [...current, toast]);
      window.setTimeout(() => {
        setToastItems((current) => current.filter((item) => item.id !== toast.id));
      }, 3600);
    };

    const handleConfirm = (event) => {
      if (!event.detail) {
        return;
      }

      setConfirmState(event.detail);
    };

    window.addEventListener("app:toast", handleToast);
    window.addEventListener("app:confirm", handleConfirm);

    return () => {
      window.removeEventListener("app:toast", handleToast);
      window.removeEventListener("app:confirm", handleConfirm);
    };
  }, []);

  useEffect(() => {
    if (!adminSession || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const query = searchQuery.trim().toLowerCase();
        const [studentRes, teacherRes] = await Promise.all([
          getStudents("all", { fields: "lookup" }),
          getTeachers("all", { fields: "lookup" })
        ]);

        const matchedStudents = studentRes.data
          .filter((student) =>
            [student.name, student.class, student.rollNumber, student.parentPhone]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(query))
          )
          .slice(0, 4)
          .map((student) => ({
            id: student._id,
            type: "student",
            title: student.name,
            subtitle: `Class ${student.class} • Roll ${student.rollNumber}`,
            meta: student.parentPhone,
            filterValue: student.class
          }));

        const matchedTeachers = teacherRes.data
          .filter((teacher) =>
            [teacher.name, teacher.subject, teacher.phone, teacher.experience]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(query))
          )
          .slice(0, 4)
          .map((teacher) => ({
            id: teacher._id,
            type: "teacher",
            title: teacher.name,
            subtitle: `${teacher.subject} • ${teacher.experience}`,
            meta: teacher.phone,
            filterValue: teacher.subject
          }));

        setSearchResults([...matchedStudents, ...matchedTeachers].slice(0, 8));
      } catch (err) {
        console.error("Global search failed:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [adminSession, searchQuery]);

  const handleLogin = (admin) => {
    setAdminSession(admin);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(admin));
    setPublicPage("home");
    const nextPath = ADMIN_ROUTE_MAP.home;
    window.history.pushState({}, "", nextPath);
    document.title = ADMIN_TITLE_MAP.home;
  };

  const handleLogout = () => {
    setAdminSession(null);
    localStorage.removeItem(STORAGE_KEY);
    setActiveTab("home");
    setSearchQuery("");
    setSearchResults([]);
    setSelectedSearchItem(null);
    setShowAccountMenu(false);
    setShowNotificationMenu(false);
    setPublicPage("about");
    window.history.pushState({}, "", PUBLIC_ROUTE_MAP.about);
    document.title = PUBLIC_TITLE_MAP.about;
  };

  const handleThemeChange = (nextTheme) => {
    setThemeMode(nextTheme);
  };

  const handleNavColorChange = (nextColor) => {
    setNavColor(nextColor);
  };

  const handleTabChange = (tab) => {
    setModuleLoading(true);
    setShowAccountMenu(false);
    setShowNotificationMenu(false);
    const safeTab = adminSession?.role !== "admin" && tab === "financePay" ? "financeFees" : tab;
    setActiveTab(safeTab);
    const nextPath = ADMIN_ROUTE_MAP[safeTab] || ADMIN_ROUTE_MAP.home;
    const nextTitle = ADMIN_TITLE_MAP[safeTab] || ADMIN_TITLE_MAP.home;
    window.history.pushState({}, "", nextPath);
    document.title = nextTitle;
    window.setTimeout(() => {
      setModuleLoading(false);
    }, 450);
  };

  const unreadNotifications = notifications.filter((notification) => !readNotificationIds.includes(notification.id));

  const openNotificationMenu = () => {
    setShowNotificationMenu(true);
    setShowAccountMenu(false);
    setReadNotificationIds((current) => {
      const nextIds = new Set([...current, ...notifications.map((notification) => notification.id)]);
      return Array.from(nextIds);
    });
  };

  const closeNotificationMenu = () => {
    setShowNotificationMenu(false);
  };

  const openAccountMenu = () => {
    setShowAccountMenu(true);
    setShowNotificationMenu(false);
  };

  const closeAccountMenu = () => {
    setShowAccountMenu(false);
  };

  const handleSearchSelect = (result) => {
    setSelectedSearchItem(result);
    setSearchQuery(result.title);
    setSearchResults([]);
    setGlobalProfileItem(result);
  };

  const handleOpenGlobalProfile = (result) => {
    setSelectedSearchItem(result);
    setGlobalProfileItem(result);
  };

  const handleGlobalEdit = (result) => {
    const editSelection = { ...result, action: "edit", stamp: Date.now() };
    setSelectedSearchItem(editSelection);
    setGlobalProfileItem(null);
    setActiveTab(result.type === "student" ? "students" : "teachers");
  };

  const handleGlobalDelete = async (result) => {
    const label = result.type === "student" ? "student" : "teacher";
    const confirmed = await confirmAppAction({
      title: `Delete ${label}`,
      message: `This will permanently remove the ${label} record from the system.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      if (result.type === "student") {
        await deleteStudent(result.id);
      } else {
        await deleteTeacher(result.id);
      }
      setGlobalProfileItem(null);
      setSearchQuery("");
      setSearchResults([]);
      showAppToast({
        type: "success",
        title: `${label.charAt(0).toUpperCase() + label.slice(1)} deleted`,
        message: `The ${label} record was removed successfully.`
      });
    } catch (err) {
      console.error(`Failed to delete ${label}:`, err);
      showAppToast({
        type: "error",
        title: `Failed to delete ${label}`,
        message: err.response?.data?.error || err.message || `The ${label} could not be removed.`
      });
    }
  };

  if (!adminSession) {
    if (publicPage === "login") {
      return (
        <LoginPage
          onLogin={handleLogin}
          onBack={() => {
            setPublicPage("about");
            window.history.pushState({}, "", PUBLIC_ROUTE_MAP.about);
            document.title = PUBLIC_TITLE_MAP.about;
          }}
        />
      );
    }

    return (
      <PublicSite
        currentPage={publicPage}
        onNavigate={(page) => {
          setPublicPage(page);
            window.history.pushState({}, "", PUBLIC_ROUTE_MAP[page] || PUBLIC_ROUTE_MAP.about);
            document.title = PUBLIC_TITLE_MAP[page] || PUBLIC_TITLE_MAP.about;
        }}
        onLoginClick={() => {
          setPublicPage("login");
          window.history.pushState({}, "", PUBLIC_ROUTE_MAP.login);
          document.title = PUBLIC_TITLE_MAP.login;
        }}
      />
    );
  }

  return (
    <div
      className={`app-shell app-shell--theme-${themeMode} app-shell--nav-${navColor} ${sidebarCollapsed ? "app-shell--sidebar-collapsed" : ""}`}
    >
      {isMobileNav && sidebarCollapsed && (
        <button
          className="app-shell__mobile-nav-trigger"
          onClick={() => setSidebarCollapsed(false)}
          title="Open modules"
        >
          <span />
          <span />
          <span />
        </button>
      )}

      {isMobileNav && !sidebarCollapsed && (
        <button
          className="app-shell__mobile-nav-backdrop"
          onClick={() => setSidebarCollapsed(true)}
          aria-label="Close navigation"
        />
      )}

      <aside
        className="app-shell__sidebar"
      >
        <Navbar
          activeTab={activeTab}
          setActiveTab={(tab) => {
            handleTabChange(tab);
            if (isMobileNav) {
              setSidebarCollapsed(true);
            }
          }}
          isMobileNav={isMobileNav}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          adminSession={adminSession}
        />
      </aside>

      <main className="app-shell__main">
        <header className="app-shell__header">
          <div className="app-shell__header-main">
            <img src="/logo.jpeg" alt="Bright Line Coaching" className="app-shell__header-logo" />
             <h2>Bright Line Classes</h2>
            <div className="app-shell__search">
              <SearchIcon />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search student or teacher"
                className="app-shell__search-input"
              />
              {(searchQuery.trim().length > 0 || searching) && (
                <div className="app-shell__search-results">
                  {searching && <p className="app-shell__search-empty">Searching...</p>}
                  {!searching && searchResults.length === 0 && (
                    <p className="app-shell__search-empty">No student or teacher found.</p>
                  )}
                  {!searching &&
                    searchResults.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        className="app-shell__search-item"
                        onClick={() => handleSearchSelect(result)}
                      >
                        <span className={`app-shell__search-tag app-shell__search-tag--${result.type}`}>
                          {result.type === "student" ? "ST" : "TR"}
                        </span>
                        <span className="app-shell__search-copy">
                          <strong>{result.title}</strong>
                          <span>{result.subtitle}</span>
                          <small>{result.meta}</small>
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
          <div className="app-shell__header-actions">
            <div
              className="app-shell__notification"
              onMouseEnter={() => {
                if (!isMobileNav) {
                  openNotificationMenu();
                }
              }}
              onMouseLeave={() => {
                if (!isMobileNav) {
                  closeNotificationMenu();
                }
              }}
            >
              <button
                className="app-shell__icon-trigger"
                onClick={() => {
                  if (isMobileNav) {
                    setShowNotificationMenu((current) => {
                      const nextValue = !current;
                      if (nextValue) {
                        openNotificationMenu();
                        return true;
                      }
                      return false;
                    });
                  }
                }}
                title="Notifications"
              >
                <BellIcon />
                {unreadNotifications.length > 0 && <span className="app-shell__notification-badge">{unreadNotifications.length}</span>}
              </button>

              {showNotificationMenu && (
                <div className="app-shell__notification-menu">
                  <div className="app-shell__menu-head">
                    <strong>Notifications</strong>
                    <span>{notifications.length} items</span>
                  </div>
                  {notifications.length === 0 && (
                    <p className="app-shell__menu-empty">No new notifications.</p>
                  )}
                  {notifications.map((notification) => (
                    <div key={notification.id} className={`app-shell__notification-item app-shell__notification-item--${notification.type}`}>
                        <strong>{notification.title}</strong>
                        <span>{notification.detail}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div
              className="app-shell__account"
              onMouseEnter={() => {
                if (!isMobileNav) {
                  openAccountMenu();
                }
              }}
              onMouseLeave={() => {
                if (!isMobileNav) {
                  closeAccountMenu();
                }
              }}
            >
              <button
                className="app-shell__account-trigger"
                onClick={() => {
                  if (isMobileNav) {
                    setShowAccountMenu((current) => {
                      const nextValue = !current;
                      if (nextValue) {
                        closeNotificationMenu();
                      }
                      return nextValue;
                    });
                  }
                }}
                title="Admin menu"
              >
                <span className="app-shell__account-avatar">
                  {(adminSession.name || adminSession.username || "A").slice(0, 1).toUpperCase()}
                </span>
              </button>

              {showAccountMenu && (
                <div className="app-shell__account-menu">
                  <div className="app-shell__account-menu-head">
                    <span className="app-shell__account-avatar app-shell__account-avatar--large">
                      {(adminSession.name || adminSession.username || "A").slice(0, 1).toUpperCase()}
                    </span>
                    <div>
                      <strong>{adminSession.name || "Admin"}</strong>
                      <span>Admin</span>
                    </div>
                  </div>
                  <div className="app-shell__settings-group">
                    <span className="app-shell__settings-label">Theme</span>
                    <div className="app-shell__toggle-row">
                      <button
                        className={`app-shell__mini-btn ${themeMode === "light" ? "active" : ""}`}
                        onClick={() => handleThemeChange("light")}
                      >
                        Light
                      </button>
                      <button
                        className={`app-shell__mini-btn ${themeMode === "dark" ? "active" : ""}`}
                        onClick={() => handleThemeChange("dark")}
                      >
                        Dark
                      </button>
                    </div>
                  </div>
                  <div className="app-shell__settings-group">
                    <span className="app-shell__settings-label">Nav Color</span>
                    <div className="app-shell__swatches">
                      {["blue", "green", "amber"].map((color) => (
                        <button
                          key={color}
                          className={`app-shell__swatch app-shell__swatch--${color} ${navColor === color ? "active" : ""}`}
                          onClick={() => handleNavColorChange(color)}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                  <button className="app-shell__account-logout" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="app-shell__content">
          {activeTab === "home" && (
            <HomePage
              adminSession={adminSession}
              currentIstTime={currentIstTime}
              festivals={festivals}
              onOpenFestival={() => setShowFestivalModal(true)}
              onNavigate={handleTabChange}
              formatToday={formatToday}
              formatHolidayDate={formatHolidayDate}
              fallbackHoliday={UPCOMING_HOLIDAY}
            />
          )}
          {activeTab === "students" && (
            <StudentList
              searchSelection={selectedSearchItem?.type === "student" ? selectedSearchItem : null}
              onOpenProfile={handleOpenGlobalProfile}
            />
          )}
          {activeTab === "teachers" && (
            <TeacherList
              searchSelection={selectedSearchItem?.type === "teacher" ? selectedSearchItem : null}
              onOpenProfile={handleOpenGlobalProfile}
            />
          )}
          {activeTab === "attendance" && <AttendancePage />}
          {activeTab === "inquiries" && <InquiriesPage />}
          {activeTab === "financeFees" && <FeesPage />}
          {activeTab === "financePay" && adminSession.role === "admin" && <SalaryPage />}
          {activeTab === "galleryAdmin" && <GalleryAdminPage />}
          {activeTab === "testMarksheet" && <TestMarksheetPage />}
          {activeTab === "birthdayWishes" && <BirthdayWishesPage />}
          {activeTab === "reports" && <ReportsPage />}
        </section>

        <footer className="app-shell__footer">
          Copyright {new Date().getFullYear()} Bright Line Classes. All rights reserved.
        </footer>
      </main>

      {globalProfileItem && (
        <GlobalProfileModal
          item={globalProfileItem}
          onClose={() => setGlobalProfileItem(null)}
          onEdit={handleGlobalEdit}
          onDelete={handleGlobalDelete}
        />
      )}

      {showFestivalModal && (
        <FestivalModal
          festivals={festivals}
          currentIstTime={currentIstTime}
          onClose={() => setShowFestivalModal(false)}
        />
      )}

      {moduleLoading && (
        <div className="app-shell__loader">
          <div className="app-shell__logo-loader">
            <img src="/rita-tech-logo.png" alt="Loading" className="app-shell__logo-loader-image" />
          </div>
        </div>
      )}

      {toastItems.length > 0 && (
        <div className="app-shell__toast-stack">
          {toastItems.map((toast) => (
            <div key={toast.id} className={`app-shell__toast app-shell__toast--${toast.type}`}>
              <div className="app-shell__toast-copy">
                <strong>{toast.title}</strong>
                {toast.message && <span>{toast.message}</span>}
              </div>
              <button
                className="app-shell__toast-close"
                onClick={() => setToastItems((current) => current.filter((item) => item.id !== toast.id))}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {confirmState && (
        <div className="app-shell__confirm-layer">
          <button
            className="app-shell__confirm-backdrop"
            onClick={() => {
              confirmState.resolve(false);
              setConfirmState(null);
            }}
            aria-label="Close confirmation"
          />
          <div className="app-shell__confirm-dialog">
            <span className={`app-shell__confirm-badge app-shell__confirm-badge--${confirmState.type || "danger"}`}>
              {confirmState.type === "success" ? "Success" : "Confirm"}
            </span>
            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>
            <div className="app-shell__confirm-actions">
              <button
                className="app-shell__confirm-btn app-shell__confirm-btn--secondary"
                onClick={() => {
                  confirmState.resolve(false);
                  setConfirmState(null);
                }}
              >
                {confirmState.cancelLabel || "Cancel"}
              </button>
              <button
                className={`app-shell__confirm-btn app-shell__confirm-btn--${confirmState.type || "danger"}`}
                onClick={() => {
                  confirmState.resolve(true);
                  setConfirmState(null);
                }}
              >
                {confirmState.confirmLabel || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
